<template>
  <Dialog :open="true" @close="props.onClose">
    <div class="fixed inset-0 flex items-center justify-center p-4 z-20 bg-zinc-700/90">
      <DialogPanel
        class="border border-white w-[800px] h-[90vh] flex flex-col bg-black overflow-hidden"
      >
        <DialogTitle class="border-b border-white p-3 text-yellow-300">
          {{ text('ui.battle_log') }}
        </DialogTitle>

        <div
          id="battle_log_body"
          class="relative w-full flex flex-col flex-1 min-h-0 overflow-hidden"
        >
          <div
            id="battle_log_labels"
            class="w-full flex text-sm font-bold p-6 pb-0 pointer-events-none"
          >
            <div class="w-[25%] text-center">{{ text('ui.opponent') }}</div>
            <div class="w-[15%] text-center">{{ text('ui.match_type') }}</div>
            <div class="w-[15%] text-center">{{ text('ui.result') }}</div>
            <div class="w-[45%] text-center">{{ text('ui.rewards') }}</div>
          </div>
          <div ref="scroll_container" class="relative w-full flex-1 overflow-y-auto">
            <canvas
              id="modal_canvas"
              ref="modal_canvas"
              :style="`height: ${state.canvas_height}px`"
              class="absolute top-0 left-0 w-full pointer-events-auto"
              style="z-index: 0"
            />
            <div class="px-6 pb-6 relative pointer-events-none no-select" style="z-index: 1">
              <div
                v-if="state.sorted_battle_log_entries.length === 0"
                class="mt-48 w-full text-center text-neutral-500 italic text-xs"
              >
                {{ text('ui.no_log_entries') }}
              </div>
              <div
                v-for="(log_entry, i) of state.sorted_battle_log_entries"
                :id="`battle_log_entry_${log_entry.battle_id}`"
                :key="log_entry.battle_id"
                class="w-full flex text-xs p-4 items-center max-h-[72px] pointer-events-none"
              >
                <div class="w-[25%] text-center pointer-events-auto">
                  <UserNameplate :username="log_entry.opponent" />
                </div>
                <div class="w-[15%] text-center pointer-events-auto">
                  {{ text(`ui.game_modes.${log_entry.game_mode}`) }}
                  {{ text(`ui.game_submodes.${log_entry.game_submode}`) }}
                  <div
                    v-if="log_entry.inn_attacker || log_entry.inn_defender"
                    class="text-[10px] text-neutral-400/80"
                  >
                    {{ log_entry.inn_attacker ? text('ui.attacking') : text('ui.defending') }}
                  </div>
                </div>
                <div
                  :class="{
                    'w-[15%] text-center pointer-events-auto': true,
                    'text-green-500': log_entry.won,
                    'text-red-500': !log_entry.won,
                  }"
                >
                  {{ log_entry.won ? text('ui.won') : text('ui.lost') }}
                </div>
                <div class="w-[35%] text-center pointer-events-none">
                  <BattleRewardsIcons
                    v-if="log_entry.rewards && state.modal_pixi_app"
                    :key="`${state.rewards_key}_${i}`"
                    :battle_id="log_entry.battle_id"
                    :modal_pixi_stage="state.modal_pixi_app.stage"
                    :rewards="log_entry.rewards"
                    class="mr-3"
                  />
                </div>
                <div
                  :class="{
                    'w-[5%] p-3 flex justify-center items-center': true,
                    invisible: claimAllBtnInvisibility(log_entry),
                  }"
                  style="font-size: 9px"
                >
                  <div
                    class="text-center text-blue-500 hover:text-yellow-300 cursor-pointer pointer-events-auto"
                    @click="onClaimAllClick(log_entry.battle_id, $event)"
                  >
                    {{ text('ui.claim_all') }}
                  </div>
                </div>
                <div class="w-[5%] flex justify-center items-center">
                  <div
                    :class="{
                      ['ml-6 p-3 text-lg font-bold text-red-800 hover:text-yellow-300 cursor-pointer']: true,
                      ['pointer-events-auto']: !state.pending_claim_all[log_entry.battle_id],
                      ['pointer-events-none']: state.pending_claim_all[log_entry.battle_id],
                    }"
                    @click="deleteLogEntry(log_entry.battle_id)"
                  >
                    <DTAnimatedElipsis
                      v-if="state.pending_claim_all[log_entry.battle_id]"
                      class="text-tiny font-extrabold"
                    />
                    <span v-else>{{ 'x' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogPanel>
    </div>
  </Dialog>
</template>

<script setup>
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/vue';
import { nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { BattleDebriefActions } from '~/flux/actions';
import { CurrencyStore, GameStateStore, NotificationsStore, PlayerStore } from '~/flux/stores';
import text from '~/text';
import CanvasTools from '~/view/CanvasTools';
import { DTAnimatedElipsis, UserNameplate } from '~/view/components/common/DOM';
import BattleRewardsIcons from './BattleRewardsIcons.vue';

const props = defineProps({
  onClose: {
    type: Function,
    required: true,
  },
});

const modal_canvas = ref(null);
const scroll_container = ref(null);

watch(modal_canvas, (val) => {
  if (val && !state.modal_pixi_app) {
    nextTick(() => {
      state.modal_pixi_app = CanvasTools.makePixiRenderer({
        canvas_id: 'modal_canvas',
        canvas_ele: val,
      });
      nextTick(() => {
        updateCanvasSize();
      });
    });
  }
});

const state = reactive({
  modal_pixi_app: null,
  rewards_key: 0,
  sorted_battle_log_entries: [],
  pending_claim_all: {},
  canvas_height: 0,
});

onMounted(() => {
  CurrencyStore.on(CurrencyStore.GOLD_REWARD_CLAIMED, onBattleLog);
  CurrencyStore.on(CurrencyStore.PD_REWARD_CLAIMED, onBattleLog);
  GameStateStore.on(GameStateStore.ITEM_REWARD_CLAIMED, onBattleLog);
  GameStateStore.on(GameStateStore.ALL_REWARDS_CLAIMED, onAllRewardsClaimed);
  NotificationsStore.on(NotificationsStore.GOT_BATTLE_LOG, onBattleLog);
  onBattleLog();
});

onBeforeUnmount(() => {
  state.modal_pixi_app?.destroy();
  CurrencyStore.removeListener(CurrencyStore.GOLD_REWARD_CLAIMED, onBattleLog);
  CurrencyStore.removeListener(CurrencyStore.PD_REWARD_CLAIMED, onBattleLog);
  GameStateStore.removeListener(GameStateStore.ITEM_REWARD_CLAIMED, onBattleLog);
  GameStateStore.removeListener(GameStateStore.ALL_REWARDS_CLAIMED, onAllRewardsClaimed);
  NotificationsStore.removeListener(NotificationsStore.GOT_BATTLE_LOG, onBattleLog);
});

function claimAllBtnInvisibility(log_entry) {
  if (!log_entry?.rewards) {
    return true;
  }

  const are_actual_items = Object.values(
    log_entry.rewards[PlayerStore.getAll().loggedInPlayerId]?.items ?? {}
  ).filter((x) => !!x).length;
  if (!are_actual_items && !log_entry.rewards?.gold && !log_entry.rewards?.pixieDust) {
    return true;
  }
  const rewards = log_entry.rewards[PlayerStore.getAll().loggedInPlayerId] || log_entry.rewards;
  return !rewards?.gold && !rewards?.pixieDust && !Object.values(rewards?.items).length;
}

function deleteLogEntry(battle_id) {
  BattleDebriefActions.deleteBattleLogEntry(battle_id);
}

function onBattleLog() {
  state.sorted_battle_log_entries = Object.entries(NotificationsStore.getAll().battleLog)
    .map(([battle_id, entry]) => ({ ...entry, battle_id }))
    .sort((a, b) => b.endedAt - a.endedAt);
  nextTick(() => {
    updateCanvasSize();
    state.rewards_key++;
  });
}

function onClaimAllClick(battle_id, event) {
  event.stopPropagation();
  BattleDebriefActions.claimAllRewards(battle_id);
  state.pending_claim_all[battle_id] = true;
}

function updateCanvasSize() {
  if (!scroll_container.value) return;

  // Get the scrollable content height
  const contentHeight = scroll_container.value.scrollHeight;
  state.canvas_height = contentHeight;

  nextTick(() => {
    if (state.modal_pixi_app && modal_canvas.value) {
      const { width } = modal_canvas.value.getBoundingClientRect();
      state.modal_pixi_app.renderer.resize(width, contentHeight);
    }
  });
}

function onAllRewardsClaimed({ log_id }) {
  delete state.pending_claim_all[log_id];
}
</script>
